import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  RefreshCw,
  Search,
  Server,
  Star,
  ChevronDown,
  ChevronRight,
  LayoutGrid,
  List,
} from "lucide-react";
import { type ColumnDef } from "@tanstack/react-table";
import { api, type AppSummary } from "@/api/client";
import { AppCard } from "@/components/AppCard";
import { EnvToggle } from "@/components/EnvToggle";
import { StatusBadge } from "@/components/shared/status-badge";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import { DataTable, SortableHeader } from "@/components/shared/data-table";
import { FleetSummary, type FleetFilter } from "@/features/dashboard/FleetSummary";
import { useFavorites } from "@/hooks/useFavorites";
import { Button, Card, CardContent, cn, Collapsible, CollapsibleContent, CollapsibleTrigger, Input, Skeleton, ToggleGroup, ToggleGroupItem } from "@fox1ck-org/ui";

type GroupBy = "wave" | "project" | "flat";

const columns: ColumnDef<AppSummary>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => <SortableHeader column={column}>Название</SortableHeader>,
    cell: ({ row }) => (
      <Link to={`/apps/${row.original.name}`} className="font-medium text-foreground hover:text-primary">
        {row.original.name}
      </Link>
    ),
  },
  {
    accessorKey: "namespace",
    header: ({ column }) => <SortableHeader column={column}>Пространство имён</SortableHeader>,
    cell: ({ row }) => <span className="text-muted-foreground">{row.original.namespace}</span>,
  },
  {
    accessorKey: "project",
    header: "Проект",
    cell: ({ row }) => <span className="text-muted-foreground">{row.original.project}</span>,
  },
  {
    accessorKey: "health",
    header: "Состояние",
    cell: ({ row }) => <StatusBadge status={row.original.health.status} type="health" />,
  },
  {
    accessorKey: "sync",
    header: "Синхронизация",
    cell: ({ row }) => <StatusBadge status={row.original.sync.status} type="sync" />,
  },
  {
    accessorKey: "images",
    header: "Образ",
    cell: ({ row }) => (
      <span className="text-muted-foreground font-mono text-xs truncate max-w-[200px] block">
        {row.original.images[0]?.split("/").pop() ?? "-"}
      </span>
    ),
  },
];

export default function Dashboard() {
  const [env, setEnv] = useState<"prod" | "dev">("prod");
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [groupBy, setGroupBy] = useState<GroupBy>("wave");
  const [filter, setFilter] = useState<FleetFilter>("all");
  const { isFavorite, toggle: toggleFavorite, favorites } = useFavorites();

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["cluster-apps", env],
    queryFn: () => api.cluster.listApps(env),
    refetchInterval: 30_000,
  });

  // Compute totals client-side. Backend `summary` lacks Progressing, so we
  // re-derive everything from the flat list for consistency.
  const totals = useMemo(() => {
    const apps = data?.apps ?? [];
    return {
      total: apps.length,
      healthy: apps.filter((a) => a.health.status === "Healthy").length,
      progressing: apps.filter((a) => a.health.status === "Progressing").length,
      degraded: apps.filter(
        (a) => a.health.status === "Degraded" || a.health.status === "Missing"
      ).length,
      outOfSync: apps.filter((a) => a.sync.status === "OutOfSync").length,
    };
  }, [data]);

  const matchesFilter = useCallback(
    (app: AppSummary): boolean => {
      switch (filter) {
        case "all":
          return true;
        case "healthy":
          return app.health.status === "Healthy";
        case "progressing":
          return app.health.status === "Progressing";
        case "degraded":
          return app.health.status === "Degraded" || app.health.status === "Missing";
        case "outOfSync":
          return app.sync.status === "OutOfSync";
      }
    },
    [filter]
  );

  const matchesSearch = useCallback(
    (app: AppSummary): boolean => {
      if (!search) return true;
      const needle = search.toLowerCase();
      return (
        app.name.toLowerCase().includes(needle) ||
        app.namespace.toLowerCase().includes(needle) ||
        app.project.toLowerCase().includes(needle)
      );
    },
    [search]
  );

  const filteredApps = useMemo(
    () => (data?.apps ?? []).filter((a) => matchesFilter(a) && matchesSearch(a)),
    [data, matchesFilter, matchesSearch]
  );

  const grouped = useMemo(() => {
    if (groupBy === "flat") {
      return [{ name: "Приложения", apps: filteredApps }];
    }
    if (groupBy === "project") {
      const byProject = new Map<string, AppSummary[]>();
      for (const app of filteredApps) {
        const list = byProject.get(app.project) ?? [];
        list.push(app);
        byProject.set(app.project, list);
      }
      return Array.from(byProject.entries())
        .map(([name, apps]) => ({ name, apps }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    // wave (matches backend categories)
    const cats = data?.categories ?? [];
    return cats
      .map((cat) => ({
        name: cat.name,
        apps: cat.apps.filter((a) => matchesFilter(a) && matchesSearch(a)),
      }))
      .filter((cat) => cat.apps.length > 0);
  }, [groupBy, filteredApps, data, matchesFilter, matchesSearch]);

  const favoriteApps = useMemo(
    () => filteredApps.filter((a) => favorites.has(a.name)),
    [filteredApps, favorites]
  );

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
        Не удалось загрузить приложения: {(error as Error).message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Обзор">
        <EnvToggle value={env} onChange={setEnv} />
        <Button variant="ghost" size="icon" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
        </Button>
      </PageHeader>

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-3 w-16 mb-2" />
                <Skeleton className="h-7 w-12" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <FleetSummary totals={totals} activeFilter={filter} onFilterChange={setFilter} />
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Поиск приложений…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        <ToggleGroup
          type="single"
          value={groupBy}
          onValueChange={(v) => v && setGroupBy(v as GroupBy)}
          size="sm"
        >
          <ToggleGroupItem value="wave" className="text-xs">Волна</ToggleGroupItem>
          <ToggleGroupItem value="project" className="text-xs">Проект</ToggleGroupItem>
          <ToggleGroupItem value="flat" className="text-xs">Списком</ToggleGroupItem>
        </ToggleGroup>

        <ToggleGroup
          type="single"
          value={viewMode}
          onValueChange={(v) => v && setViewMode(v as "grid" | "table")}
          size="sm"
        >
          <ToggleGroupItem value="grid" aria-label="Плитка"><LayoutGrid className="h-4 w-4" /></ToggleGroupItem>
          <ToggleGroupItem value="table" aria-label="Таблица"><List className="h-4 w-4" /></ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-5 w-16 rounded-md" />
                </div>
                <Skeleton className="h-5 w-20 rounded-md" />
                <Skeleton className="h-3 w-40" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : viewMode === "grid" ? (
        <div className="space-y-6">
          {favoriteApps.length > 0 && (
            <CategorySection
              name={
                <span className="flex items-center gap-2">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  Избранное
                </span>
              }
              count={favoriteApps.length}
              defaultOpen
            >
              <Grid>
                {favoriteApps.map((app) => (
                  <AppCard
                    key={app.name}
                    app={app}
                    isFavorite
                    onToggleFavorite={toggleFavorite}
                  />
                ))}
              </Grid>
            </CategorySection>
          )}

          {grouped.map((category) =>
            category.apps.length > 0 ? (
              <CategorySection key={category.name} name={category.name} count={category.apps.length}>
                <Grid>
                  {category.apps.map((app) => (
                    <AppCard
                      key={app.name}
                      app={app}
                      isFavorite={isFavorite(app.name)}
                      onToggleFavorite={toggleFavorite}
                    />
                  ))}
                </Grid>
              </CategorySection>
            ) : null
          )}

          {filteredApps.length === 0 && (
            <EmptyState
              icon={<Server className="h-12 w-12" />}
              title="Приложений нет"
              description={
                search || filter !== "all"
                  ? "Попробуйте снять фильтр или поиск."
                  : "В этом окружении нет приложений."
              }
            />
          )}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filteredApps}
          emptyState={
            <EmptyState
              icon={<Server className="h-10 w-10" />}
              title="Приложений нет"
              description="Попробуйте снять фильтр или поиск."
            />
          }
        />
      )}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
      {children}
    </div>
  );
}

function CategorySection({
  name,
  count,
  children,
  defaultOpen = true,
}: {
  name: React.ReactNode;
  count: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 mb-3 group cursor-pointer">
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <h2 className="text-sm font-semibold text-foreground">{name}</h2>
        <span className="text-xs text-muted-foreground">({count})</span>
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  );
}
